import { useState } from "react";
import { motion } from "framer-motion";
import { Reveal, SectionHead, Btn } from "../Bits";
import { PLANNER } from "../../lib/site";

const BLUE = "#0F4C81";
const IDLE = "#DEDEDA";
const LINE = "rgba(17,17,17,0.25)";

const FloorPlan = ({ active, onSelect }) => {
  const on = (id) => active === id;
  const zone = (id) => ({
    onClick: () => onSelect(id),
    className: "cursor-pointer",
    style: { transition: "fill 400ms ease, stroke 400ms ease, opacity 400ms ease" },
  });

  return (
    <svg viewBox="0 0 900 560" className="h-full w-full" role="img" aria-label="Facility floor plan">
      <rect x="0" y="0" width="900" height="560" fill="#FFFFFF" />
      <g stroke="rgba(17,17,17,0.06)" strokeWidth="1">
        {Array.from({ length: 17 }).map((_, i) => (
          <line key={`v${i}`} x1={i * 56} y1="0" x2={i * 56} y2="560" />
        ))}
        {Array.from({ length: 11 }).map((_, i) => (
          <line key={`h${i}`} x1="0" y1={i * 56} x2="900" y2={i * 56} />
        ))}
      </g>

      {/* room shell */}
      <rect x="46" y="46" width="808" height="468" fill="#FFFFFF" stroke="#111111" strokeWidth="2" />

      {/* wall pads */}
      <g {...zone("walls")} data-testid="plan-zone-walls">
        {[
          [48, 48, 804, 18],
          [48, 48, 18, 464],
          [834, 48, 18, 464],
        ].map(([x, y, w, h], i) => (
          <rect key={`wall-${i}`} x={x} y={y} width={w} height={h} fill={on("walls") ? BLUE : IDLE} />
        ))}
      </g>

      {/* main training surface */}
      <g {...zone("surface")} data-testid="plan-zone-surface">
        <rect
          x="116"
          y="112"
          width="668"
          height="336"
          fill={on("surface") ? "rgba(15,76,129,0.16)" : "#F2F2EE"}
          stroke={on("surface") ? BLUE : LINE}
          strokeWidth={on("surface") ? 3 : 1.5}
        />
        {Array.from({ length: 7 }).map((_, i) => (
          <line
            key={`mv${i}`}
            x1={116 + (i + 1) * 83.5}
            y1="112"
            x2={116 + (i + 1) * 83.5}
            y2="448"
            stroke={LINE}
            strokeWidth="0.75"
          />
        ))}
        {Array.from({ length: 3 }).map((_, i) => (
          <line
            key={`mh${i}`}
            x1="116"
            y1={112 + (i + 1) * 84}
            x2="784"
            y2={112 + (i + 1) * 84}
            stroke={LINE}
            strokeWidth="0.75"
          />
        ))}
      </g>

      {/* colour zones — border band */}
      <g {...zone("zones")} data-testid="plan-zone-zones">
        <rect
          x="128"
          y="124"
          width="644"
          height="312"
          fill="none"
          stroke={on("zones") ? BLUE : "#C9C9C4"}
          strokeWidth={on("zones") ? 16 : 10}
          opacity={on("zones") ? 1 : 0.55}
        />
      </g>

      {/* competition area */}
      <g {...zone("competition")} data-testid="plan-zone-competition">
        <rect
          x="286"
          y="168"
          width="328"
          height="224"
          fill="none"
          stroke={on("competition") ? BLUE : "#9A9A94"}
          strokeWidth={on("competition") ? 3 : 1.5}
          strokeDasharray="10 8"
        />
      </g>

      {/* corners and columns */}
      <g {...zone("corners")} data-testid="plan-zone-corners">
        {[
          [46, 46],
          [826, 46],
          [46, 486],
          [826, 486],
        ].map(([x, y]) => (
          <rect
            key={`c${x}-${y}`}
            x={x}
            y={y}
            width="28"
            height="28"
            fill={on("corners") ? BLUE : "#B9B9B4"}
          />
        ))}
        {[
          [286, 92],
          [586, 92],
        ].map(([x, y]) => (
          <rect
            key={`col${x}`}
            x={x}
            y={y}
            width="26"
            height="26"
            fill={on("corners") ? BLUE : "#B9B9B4"}
            stroke="#111111"
            strokeWidth="1"
          />
        ))}
      </g>

      {/* custom logo */}
      <g {...zone("logos")} data-testid="plan-zone-logos">
        <text
          x="450"
          y="292"
          textAnchor="middle"
          fontFamily="Sora, sans-serif"
          fontWeight="700"
          fontSize="52"
          letterSpacing="6"
          fill={on("logos") ? BLUE : "#D2D2CD"}
        >
          MAKO
        </text>
      </g>

      {/* annotations */}
      <g fontFamily="Manrope, sans-serif" fontSize="11" letterSpacing="2" fill="#7A7F85">
        <text x="46" y="536">ROOM PLAN — INDICATIVE</text>
        <text x="854" y="536" textAnchor="end">
          SELECT A LAYER
        </text>
      </g>
    </svg>
  );
};

const Planner = () => {
  const [active, setActive] = useState(PLANNER[0].id);
  const item = PLANNER.find((p) => p.id === active);

  return (
    <section
      data-testid="home-planner"
      className="border-t border-[#111111]/10 bg-[#F5F5F2] py-20 lg:py-32"
    >
      <div className="mx-auto max-w-[1400px] px-6 lg:px-10">
        <Reveal>
          <SectionHead
            eyebrow="Facility planning"
            title="Build the whole space"
            copy="From flooring to wall protection, MAKO can help create a complete surface system around your room, discipline and brand. Select a layer to see how it sits in the room."
          />
        </Reveal>

        <div className="mt-14 grid gap-12 lg:mt-20 lg:grid-cols-12 lg:gap-16">
          <Reveal className="lg:col-span-7">
            <div className="relative border border-[#111111]/10 bg-white">
              <FloorPlan active={active} onSelect={setActive} />
              <div className="absolute inset-x-0 bottom-0 h-[3px] bg-[#0F4C81]" />
            </div>
          </Reveal>

          <div className="lg:col-span-5">
            <div className="flex flex-wrap gap-2.5" data-testid="planner-labels">
              {PLANNER.map((p) => (
                <button
                  key={p.id}
                  onClick={() => setActive(p.id)}
                  aria-pressed={active === p.id}
                  data-testid={`planner-label-${p.id}`}
                  className={`border px-4 py-2.5 text-[0.6875rem] font-bold uppercase tracking-[0.14em] transition-colors duration-300 ${
                    active === p.id
                      ? "border-[#0F4C81] bg-[#0F4C81] text-white"
                      : "border-[#111111]/15 bg-white text-[#3B3B3B] hover:border-[#0F4C81] hover:text-[#0F4C81]"
                  }`}
                >
                  {p.label}
                </button>
              ))}
            </div>

            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4 }}
              className="mt-10 border-l-2 border-[#0F4C81] pl-6"
              data-testid="planner-detail"
            >
              <p className="font-display text-lg font-bold uppercase tracking-tight text-[#111111]">
                {item.label}
              </p>
              <p className="mt-4 text-sm leading-relaxed text-[#3B3B3B]">{item.copy}</p>
            </motion.div>

            <div className="mt-11">
              <Btn to="/custom-solutions" data-testid="planner-cta">
                Plan your space
              </Btn>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Planner;
